import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { organizationJsonLd, websiteJsonLd } from "@/lib/seo";
import Logo3D from "@/components/hud/Logo3D";
import { Telemetry } from "@/components/hud/HudChrome";
import { ChevronRight, Cpu, Layers, Zap, Lock } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1552957091-7d7113198882?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";

export default function Landing() {
  const [preview, setPreview] = useState({ environments: [], personas: [], tiers: [] });

  useEffect(() => {
    api.get("/content/preview").then((r) => setPreview(r.data)).catch(() => {});
  }, []);

  return (
    <div className="bg-app text-slate-200" data-testid="landing-page">
      <Seo path="/" jsonLd={[organizationJsonLd, websiteJsonLd]} />
      {/* HERO */}
      <section className="relative min-h-[92vh] flex flex-col items-center justify-center overflow-hidden">
        <img
          src={HERO_IMG}
          alt="environment"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
          style={{ mixBlendMode: "luminosity" }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-app via-app/85 to-app/40" />
        <div className="absolute inset-0 hud-grid opacity-40" />

        <div className="relative z-10 flex flex-col items-center text-center px-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1 }}
          >
            <Logo3D size={170} />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="mt-8"
          >
            <div className="font-mono text-[11px] tracking-[0.45em] text-cyan/70 mb-4">
              [ IMMERSIVE SIMULATION PLATFORM ]
            </div>
            <h1 className="font-head text-5xl sm:text-6xl lg:text-7xl font-bold uppercase tracking-tight leading-[0.95]">
              Enter The <span className="text-cyan text-glow-cyan">Aether</span>
              <br /> Simulation
            </h1>
            <p className="mt-6 max-w-xl mx-auto text-lg text-slate-400 tracking-wide">
              Generate living sci-fi worlds. Speak with intelligent personas. Command a
              high-fidelity, VFX-grade HUD reality — gated by tier, limited only by ambition.
            </p>
            <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
              <Link
                to="/auth"
                data-testid="hero-cta-initiate"
                className="btn-hud group flex items-center gap-2 border border-cyan text-cyan px-7 py-3 text-sm box-glow-cyan hover:bg-cyan hover:text-app"
              >
                Initiate Uplink
                <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/pricing"
                data-testid="hero-cta-tiers"
                className="btn-hud border border-slate-600 text-slate-300 px-7 py-3 text-sm hover:border-amber hover:text-amber"
              >
                View Tiers
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Telemetry />

      {/* FEATURES */}
      <section className="mx-auto max-w-7xl px-5 py-24">
        <SectionLabel>Core Systems</SectionLabel>
        <div className="grid md:grid-cols-3 gap-6 mt-10">
          {[
            { icon: Layers, t: "Simulated Environments", d: "Step into bioluminescent jungles, orbital ringscapes, and void cathedrals — each a distinct, generative sector." },
            { icon: Cpu, t: "Model-Agnostic Engine", d: "A layered framework routes each tier to the right intelligence — from fast generation to advanced reasoning." },
            { icon: Zap, t: "VFX-Grade Personas", d: "Converse with synthetic intelligences that stay fully in character inside your chosen world." },
          ].map((f, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.12 }}
              className="glass clip-chamfer p-7 brackets hover:box-glow-cyan transition-shadow"
            >
              <f.icon className="w-7 h-7 text-cyan mb-4" />
              <h3 className="font-head text-lg uppercase tracking-wide text-slate-100">{f.t}</h3>
              <p className="mt-3 text-slate-400 leading-relaxed">{f.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ENVIRONMENTS SHOWCASE */}
      <section className="mx-auto max-w-7xl px-5 pb-24">
        <SectionLabel>Sector Registry</SectionLabel>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10" data-testid="env-showcase">
          {preview.environments?.map((env, i) => (
            <motion.div
              key={env.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: (i % 3) * 0.1 }}
              className="group relative aspect-[4/3] overflow-hidden border border-cyan/15 hover:border-cyan/60 transition-colors"
            >
              <img src={env.image} alt={env.name} className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-90 group-hover:scale-105 transition-all duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-app via-app/30 to-transparent" />
              <div className="absolute top-3 right-3 font-mono text-[9px] uppercase tracking-widest px-2 py-1 border" style={{ color: env.accent, borderColor: env.accent + "66" }}>
                {env.tier_required}
              </div>
              <div className="absolute bottom-0 left-0 p-4">
                <div className="font-mono text-[10px] tracking-widest text-cyan/70">{env.biome}</div>
                <div className="font-head text-lg uppercase tracking-wide" style={{ color: env.accent }}>{env.name}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* PERSONAS */}
      <section className="mx-auto max-w-7xl px-5 pb-24">
        <SectionLabel>Persona Array</SectionLabel>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
          {preview.personas?.map((p) => (
            <div key={p.id} className="glass p-5 brackets" data-testid={`persona-card-${p.id}`}>
              <div className="flex items-center justify-between">
                <span className="font-head text-xl tracking-widest" style={{ color: p.accent }}>{p.name}</span>
                {p.premium && <Lock className="w-3.5 h-3.5 text-amber" />}
              </div>
              <div className="font-mono text-[10px] tracking-widest text-slate-500 mt-1">{p.role.toUpperCase()}</div>
              <p className="mt-3 text-sm text-slate-400 italic leading-snug">"{p.greeting}"</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 border-t border-cyan/15">
        <div className="absolute inset-0 hud-grid opacity-30" />
        <div className="relative mx-auto max-w-3xl px-5 text-center">
          <h2 className="font-head text-4xl sm:text-5xl uppercase font-bold tracking-tight">
            Your <span className="text-amber text-glow-amber">Simulation</span> Awaits
          </h2>
          <p className="mt-4 text-slate-400 text-lg">Free access begins the moment you uplink. No card required.</p>
          <Link
            to="/auth"
            data-testid="footer-cta"
            className="btn-hud inline-flex mt-8 border border-cyan text-cyan px-8 py-3 text-sm box-glow-cyan hover:bg-cyan hover:text-app"
          >
            Begin Free
          </Link>
        </div>
      </section>

      <footer className="border-t border-cyan/10 py-8 text-center font-mono text-[10px] tracking-[0.3em] text-slate-600">
        AETHER © 2026 — IMMERSIVE SIMULATION FRAMEWORK
      </footer>
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <div className="flex items-center gap-3">
      <span className="text-amber">◆</span>
      <span className="font-mono text-xs uppercase tracking-[0.3em] text-cyan/80">{children}</span>
      <div className="flex-1 h-px bg-gradient-to-r from-cyan/30 to-transparent" />
    </div>
  );
}
